import React, { useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useSearchParams, Link } from "react-router-dom";
import { apiPublicPost } from "../DataService/apiClient";
import { Button } from "../Components/Button";
import { Icon } from "../Icons/Icon";
import "./SignIn.css";
import FadeIn from "../Animations/FadeIn";

const passwordRules = [
  { label: "Minst 6 tecken", test: (value: string) => value.length >= 6 },
  { label: "Minst en liten bokstav", test: (value: string) => /[a-z]/.test(value) },
  { label: "Minst en versal", test: (value: string) => /[A-Z]/.test(value) },
  { label: "Minst en siffra", test: (value: string) => /[0-9]/.test(value) },
  { label: "Minst ett specialtecken", test: (value: string) => /[^a-zA-Z0-9]/.test(value) },
];

export const ResetPassword = () => {
  console.log("ResetPassword mounted");
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";
  console.log("ResetPassword params", { email, tokenLength: token.length, raw: searchParams.toString() });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const passwordErrors = useMemo(() => {
    return passwordRules.filter(rule => !rule.test(newPassword)).map(rule => rule.label);
  }, [newPassword]);

  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  const canSubmit = useMemo(
    () => Boolean(email && token && newPassword && confirmPassword && passwordErrors.length === 0 && newPassword === confirmPassword),
    [email, token, newPassword, confirmPassword, passwordErrors.length]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Lösenorden matchar inte.");
      return;
    }

    if (passwordErrors.length > 0) {
      toast.error("Lösenordet uppfyller inte kraven.");
      return;
    }

    setIsLoading(true);

    try {
      await apiPublicPost("/Auth/set-password", {
        email,
        token,
        newPassword,
      });

      setDone(true);
      toast.success("Ditt lösenord är uppdaterat.");
    } catch (err) {
      console.error(err);
      toast.error("Kunde inte uppdatera lösenordet.");
    }

    setIsLoading(false);
  };

  return (
    <div className="signin-page">
      <FadeIn className="signin-card">
        <div className="signin-header">
          <div className="signin-icon">
            <Icon.LogIn width={36} height={36} />
          </div>
          <h1 className="signin-title">Välj nytt lösenord</h1>
          <p className="signin-subtitle">För {email || "ditt konto"}</p>
          <p className="reset-debug">Reset mode active</p>
        </div>

        {done ? (
            <div className="signin-reset-success">
              <p>Lösenordet är sparat.</p>
              <Link to="/sign-in">Gå till inloggning</Link>
            </div>
        ) : (
          <form onSubmit={handleSubmit} className="signin-form">
            <div className="signin-field">
              <label htmlFor="newPassword">Nytt lösenord</label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
              />
              <div className="reset-password-rules">
                <div className={`reset-password-rule ${passwordErrors.length === 0 ? "ok" : "missing"}`}>
                  <Icon.CheckMarkCircle color={passwordErrors.length === 0 ? "#8fae70" : "#c9b8b2"} />
                  <span>
                    {passwordErrors.length === 0
                      ? "Lösenordskrav uppfyllda"
                      : `${passwordErrors.length} krav saknas`}
                  </span>
                </div>
                <button
                  type="button"
                  className="reset-password-details-toggle"
                  onClick={() => setShowPasswordRequirements(prev => !prev)}
                >
                  {showPasswordRequirements ? "Dölj detaljer" : "Visa detaljer"}
                </button>

                {showPasswordRequirements && (
                  <div className="reset-password-details">
                    {passwordRules.map(rule => {
                      const ok = rule.test(newPassword);
                      return (
                        <div key={rule.label} className={`reset-password-detail-row ${ok ? "ok" : "missing"}`}>
                          <Icon.CheckMarkCircle color={ok ? "#8fae70" : "#c9b8b2"} />
                          <span>{rule.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="signin-field">
              <label htmlFor="confirmPassword">Bekräfta lösenord</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
              <div className={`reset-password-match ${confirmPassword.length === 0 ? "idle" : passwordsMatch ? "ok" : "missing"}`}>
                <Icon.CheckMarkCircle color={confirmPassword.length === 0 ? "#c9b8b2" : passwordsMatch ? "#8fae70" : "#c9b8b2"} />
                <span>
                  {confirmPassword.length === 0
                    ? "Bekräfta lösenordet"
                    : passwordsMatch
                      ? "Lösenorden matchar"
                      : "Lösenorden matchar inte"}
                </span>
              </div>
            </div>

            <Button
              type="submit"
              className="signin-submit"
              disabled={isLoading || !canSubmit}
              text={isLoading ? "Sparar..." : "Spara lösenord"}
            />
          </form>
        )}
      </FadeIn>

      <ToastContainer />
    </div>
  );
};
